"use client";

import React from "react";
import { ArrowRight, FileCheck2, GraduationCap, Microscope } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "app-consultation",
  title: "Application Consultation",
  description: "Expert guidance on analytical method feasibility, instrument selection, and workflow optimization.",
  link: "/ApplicationConsultationPage",
  iconName: "microscope"
}, {
  id: "qual-lifecycle",
  title: "Qualification & Lifecycle Services",
  description: "Complete IQ/OQ qualification, preventative maintenance, and compliance support for regulated laboratories.",
  link: "/QualificationLifecycleServicesPage",
  iconName: "file-check"
}, {
  id: "training-support",
  title: "Training & Application Support",
  description: "Hands-on operator training, troubleshooting assistance, and continuing chemometrics support.",
  link: "/TrainingApplicationSupportPage",
  iconName: "graduation-cap"
}];
export default function SupportServices() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "microscope":
        return <Microscope className="size-6 text-primary" aria-hidden="true" />;
      case "file-check":
        return <FileCheck2 className="size-6 text-primary" aria-hidden="true" />;
      case "graduation-cap":
        return <GraduationCap className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <Microscope className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="supportservicesgrid" data-nav="light" className="bg-background py-16 sm:py-24">
      <div className="page-container">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p className="text-primary font-medium text-sm tracking-wide uppercase mb-2">
            Specialist Services
          </p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            End-to-End Support for Regulated Laboratories
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg">
            From initial method feasibility to ongoing compliance and team training, our application specialists support your team across the entire instrument lifecycle.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <motion.div key={service.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40">
                <div>
                  <CardHeader className="pb-4">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center mb-4">
                      {renderIcon(service.iconName)}
                    </div>
                    <CardTitle className="text-xl text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>
                <CardFooter className="pt-4 border-t border-border/50">
                  <Button asChild variant="ghost" className="p-0 h-auto font-medium text-primary hover:text-primary hover:bg-transparent group">
                    <Link to={service.link} className="inline-flex items-center gap-2">
                      <span>Explore Service</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}